import React, { useState, useEffect } from "react";
import { API } from "aws-amplify";
import { listMaterials, listOrders } from "./graphql/queries";
import { updateMaterial } from "./graphql/mutations";
import { withAuthenticator, Button, Heading } from "@aws-amplify/ui-react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "./ViewAllStockPage.css";
import { ChevronUp, ChevronDown } from "lucide-react";

const UpdateOnOrderPage = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState({});
  const { user } = useAuthenticator();

  const privileged = new Set([
    "jim.kelsey@tyrolit.com",
    "justin.willis@tyrolit.com",
    "james_kelsey@hotmail.com",
  ]);
  const isPrivileged = privileged.has(user.attributes.email);

  useEffect(() => {
    fetchToOrderItems();
  }, []);

  async function fetchToOrderItems() {
    setLoading(true);
    try {
      let nextToken = null;
      let all = [];
      do {
        const res = await API.graphql({
          query: listMaterials,
          variables: { nextToken },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
        all = all.concat(res.data.listMaterials.items);
        nextToken = res.data.listMaterials.nextToken;
      } while (nextToken);

      // Filter for live materials and calculate what should be ordered
      const toOrder = [];
      for (const m of all) {
        if (!m.Live) continue;


        // Fetch sum of orders for this material
        let orderTotal = 0;
        let ordersNextToken = null;
        do {
          const ordersRes = await API.graphql({
            query: listOrders,
            variables: {
              filter: { materialID: { eq: m.id } },
              nextToken: ordersNextToken,
            },
            authMode: "AMAZON_COGNITO_USER_POOLS",
          });
          const orders = ordersRes.data.listOrders.items || [];
          orderTotal += orders.reduce((sum, o) => sum + (o.Quantity || 0), 0);
          ordersNextToken = ordersRes.data.listOrders.nextToken;
        } while (ordersNextToken);

        // Calculate what needs to be ordered using live order total, not stale On_Order field
        const diff = m.Shelf_Level_Target - (m.Shelf_Level_Actual + orderTotal);

        // Only include if there's a discrepancy
        if (diff > 0) {
          toOrder.push({
            id: m.id,
            ToolNumber: m.ToolNumber,
            Dim_Value: m.Dim_Value,
            diff: diff,
            orderTotal: orderTotal,
          });
        }
      }

      setMaterials(toOrder);

      // initialize overrides to diff for each item
      const initOverrides = toOrder.reduce((acc, i) => {
        acc[i.id] = i.diff;
        return acc;
      }, {});
      setOverrides(initOverrides);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  // increment or decrement override for a given id
  const changeQty = (id, delta) => {
    setOverrides((o) => {
      const next = { ...o };
      const newVal = (next[id] || 0) + delta;
      next[id] = newVal < 0 ? 0 : newVal;
      return next;
    });
  };

  // use the override value
  const getQty = (item) => overrides[item.id] ?? item.diff;

  async function applyUpdates() {
    // Update Material.On_Order to equal the sum of all orders for each material
    for (const item of materials) {
      try {
        await API.graphql({
          query: updateMaterial,
          variables: {
            input: {
              id: item.id,
              On_Order: item.orderTotal,
            },
          },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
      } catch (err) {
        console.error(`Failed to update ${item.ToolNumber}:`, err);
      }
    }
    fetchToOrderItems();
  }
  function EmailButton({ items, onApplyUpdates }) {
    if (!items.length) return null;

    const subject = "PTG Holroyd Order Request";
    const leadIn =
      "Please can we order the following for Precision Components Ltd (18139947) from the open blanket order. " +
      "If there is more than one open blanket order please get in touch.";

    const lines = [
      "Hi,",
      "",
      leadIn,
      "",
      ...items.map(
        (i) => `${i.ToolNumber} | ${i.Dim_Value} | ${getQty(i)} off`
      ),
      "",
      "Best Regards,",
    ];

    const body = encodeURIComponent(lines.join("\r\n"));
    const href = `mailto:gborder@tyrolit.com?subject=${encodeURIComponent(
      subject
    )}&body=${body}`;

    // click handler
    const handleClick = async (e) => {
      e.preventDefault(); // stop the anchor from immediately navigating
      await onApplyUpdates(); // wait for your updates to finish
      window.location.href = href; // then open the mail client
    };

    return (
      <p>
        <a
          href={href}
          onClick={handleClick}
          style={{
            display: "inline-block",
            padding: "8px 12px",
            backgroundColor: "#007bff",
            color: "#fff",
            textDecoration: "none",
            borderRadius: "4px",
          }}
        >
          Email Items to Order
        </a>
      </p>
    );
  }

  if (!isPrivileged) {
    return <p>You do not have permission to view this page.</p>;
  }

  return (
    <div className="ViewAllStockPage">
      <Heading level={2}>Materials To Order</Heading>
      {loading ? (
        <p>Loading...</p>
      ) : materials.length > 0 ? (
        <>
          <table className="stock-table">
            <thead>
              <tr>
                <th>Tool Number</th>
                <th>Dimensions</th>
                <th>Qty to Order</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((item) => (
                <tr key={item.id}>
                  <td>{item.ToolNumber}</td>
                  <td>{item.Dim_Value}</td>
                  <td style={{ display: "flex", alignItems: "center" }}>
                    <ChevronDown
                      size={16}
                      style={{ cursor: "pointer", marginRight: 6 }}
                      onClick={() => changeQty(item.id, -1)}
                    />
                    <span>{getQty(item)}</span>
                    <ChevronUp
                      size={16}
                      style={{ cursor: "pointer", marginLeft: 6 }}
                      onClick={() => changeQty(item.id, +1)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="button-group">
            <EmailButton items={materials} onApplyUpdates={applyUpdates} />
          </div>
        </>
      ) : (
        <p>No materials require ordering.</p>
      )}
    </div>
  );
};

export default withAuthenticator(UpdateOnOrderPage, {
  hideSignUp: true,
});
